import {ServiceBase} from "../ServiceBase";
import {IPlayerOfferingsService, IShowOfferOptions} from "./player-offerings.service.interface";
import {action, observable} from "mobx";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {Check} from "@sparkware/app-utils";
import {BalanceType} from "../../../common/const/wallet_balance_type";
import { CmsBannerId } from "../cms/cms-banners/cm-banner-id/cms-banner-id";
import { OfferConfiguration } from "../configurations/config-data-sections/link-to-offer.configuration";
import { WelcomeOffer } from "./player-offerings/offers/welcome-offer";
import { WheelOfFortuneOffer } from "./player-offerings/offers/wheel-of-fortune-offer";
import React from "react";
import { IModalDialogHandler } from "../dialog/dialog.service.interface";
import { LinkToOfferBannerBox } from "../../components/link-to-offer/boxes/link-to-offer-banner/link-to-offer-banner.box";
import { CmsBannerComponent } from "../../components/cms-banner/cms-banner.component";
import { namedSetTimeout } from "../../../common/utils/time-functions";
import { RegistrationOffer } from "./player-offerings/offers/registration-offer";
import { OnGameCloseParams, OnGameOpenParams } from "../sdk/sdk-events";
import { removeSpecialCharacters } from "../../../utils/string-helpers";

const ANIMATE_WHEEL_BALANCE_THRESHOLD = 500; // 500 = 5.00(Dollars/Euros/GBPs)

export interface IOffer {
    shouldBeVisible(): boolean;
    isHighPriorityOffer(): boolean;
    render(): React.ReactNode;
    shouldUseLinkToOfferAnimation(): boolean;
    readonly id: string;
    readonly bannerId: CmsBannerId;
    readonly analyticsCategory: string;
}


export class PlayerOfferingsService extends ServiceBase implements IPlayerOfferingsService {

    @observable.ref
    enableWheelOfFortuneAnimation: boolean = false;

    @observable.ref
    private _hasWheelOfFortunePromotion: boolean = false;

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._subscribe();
    }

    get isWheelOfFortuneEnabled() {
        const isEnabledFromConfig = this.services.configuration.servicesConfigs.PlayerOfferings.isWheelOfFortuneEnabled;
        const hasWheelOfFortuneConfigSection = !Check.isNullOrUndefined(this.services.configuration.data.wheelOfFortune);
        return isEnabledFromConfig && hasWheelOfFortuneConfigSection;
    }

    get isRegistrationOfferEnabled(): boolean {
        const isEnabledFromConfig = this.services.configuration.servicesConfigs.PlayerOfferings.isRegistrationOfferEnabled;
        const hasRegistrationOfferConfigSection = !Check.isNullOrUndefined(this.services.configuration.data.registrationOffer);
        return isEnabledFromConfig && hasRegistrationOfferConfigSection;
    }

    @action
    private _setWOFEnableAnimation(enableAnimation: boolean) {
        this.enableWheelOfFortuneAnimation = enableAnimation;
    }

    
    @action
    private _setHasWheelOfFortunePromotion(hasWheelOfFortunePromotion: boolean) {
        this._hasWheelOfFortunePromotion = hasWheelOfFortunePromotion;
    }

    private _subscribe() {
        this.reaction(() => ({
                isAuthorized: this.services.user.isAuthorized,
                userBalance: this.services.walletProxy.plainNumberBankroll[BalanceType.BOTH]
            }),
            ({isAuthorized, userBalance}) => {
                if (this.isWheelOfFortuneEnabled) {
                    if (isAuthorized && Check.isNumber(userBalance) && userBalance < ANIMATE_WHEEL_BALANCE_THRESHOLD) {
                        this._setWOFEnableAnimation(true);
                    } else if(!isAuthorized){
                        this._setWOFEnableAnimation(false);
                    }
                }
            },
            {fireImmediately: true}
        );

        this.reaction(() => this.services.user.isAuthorized,
            (isAuthorized) => {
                if (!isAuthorized) {
                    this._hasWheelOfFortunePromotion = false;
                } else {
                    this._updateWheelOfFortuneAvailability();
                }
            }, { fireImmediately: true }
        );

        this.reaction(
            () => { return {
                isAuthorized: this.services.user.isAuthorized,
                hasWheelOfFortunePromotion: this.hasWheelOfFortunePromotion,
                welcomeOfferConfig: this._welcomeOfferConfig
            }},
            (data) => {
                this._updateOfferToShow();
            }, { fireImmediately: true }
        );

        this.services.sdk.events.onPromotionsChanged(data => {
            if (this.services.user.isAuthorized &&
                this.services.user.cid.toString() === data.Cid.toString() && 
                Check.isBoolean(data.IsPromotionDisplayedInClient)) {
                //this data.IsPromotionDisplayedInClient info seems to be wrong
                //this._setHasWheelOfFortunePromotion(data.IsPromotionDisplayedInClient);
                //so use API instead
                this._updateWheelOfFortuneAvailability();
            }
        });

        if (this.isRegistrationOfferEnabled) {
            this.services.runningGame.listenForLastOpenDemoGame();

            this.services.sdk.events.onGameClose((data: OnGameCloseParams) => {
                if (!data.isReal && !this.hasOpenedPopup && !this._preventShowingRegistrationPopupThisDemoSession) {
                    this._showRegistrationPopup();
                }
            });
            
            this.services.sdk.events.onGameOpen((data: OnGameOpenParams) => {
                if (!data.isReal) {
                    this.closeCurrentPopup();
                    this._preventShowingRegistrationPopupThisDemoSession = false;
                    this._initPopupTimer();
                }
            });
        }
    }

    @observable.ref
    currentLinkToOffer: IOffer | null = null;

    @action
    private _setCurrentLinkOffer(offer: IOffer): void {
        this.currentLinkToOffer = offer;
    }


    @observable.ref
    _welcomeOfferConfig: OfferConfiguration | null = null;

    @action
    setWelcomeOfferConfig(config: OfferConfiguration | null): void {
        this._welcomeOfferConfig = config;
    }

    private _getAllOffers(): IOffer[] {
        const offers: IOffer[] = [];
        // order of this insertions matter!
        if (this.isWheelOfFortuneEnabled && this.services.configuration.data.wheelOfFortune) {
            offers.push(new WheelOfFortuneOffer(this.services, this.services.configuration.data.wheelOfFortune));
        }
        if (this._welcomeOfferConfig) {
            offers.push(new WelcomeOffer(this.services, this._welcomeOfferConfig));
        }

        return offers;
    }


    async _updateOfferToShow() {

        //offer to show is first visible one
        
        const offers = this._getAllOffers();
        let selectedOffer: IOffer = null;
        for (const offer of offers) {
            if (offer.shouldBeVisible()) {
                try {
                    // make sure we have banner content
                    const hasBannerContent = await this.services.cmsBanners.loadBanner(offer.bannerId);
                    if (hasBannerContent) {
                        selectedOffer = offer;
                        break;
                    }
                } catch {
                    //we just continue to next available offer if we failed to load CMS banner
                }
        
            }
        }

        this._setCurrentLinkOffer(selectedOffer);
    }

    private async _fetchUserHasWheelOfFortune() {
        try {
            const promoCodes = await this.services.api.bonusManagerController.getEligiblePromotionCodes();
            return Check.isArray(promoCodes) && promoCodes.length >= 1;
        }
        catch (err) {
            // we might receive an error like LimitReached (122), which anyway means that we don't have any promotion codes
            return false;
        }
    }

    private _updateWheelOfFortuneAvailability() {
        if (this.isWheelOfFortuneEnabled) {
            this._fetchUserHasWheelOfFortune().then(hasWheelOfFortune => {
                //make sure user is still authenticated when we set this response
                if (this.services.user.isAuthorized) {
                    this._setHasWheelOfFortunePromotion(hasWheelOfFortune);
                }
            });
        }
    }

    get hasWheelOfFortunePromotion(): boolean {
        return this._hasWheelOfFortunePromotion;
    }

    private _shownOffer: IOffer = null;

    showOfferPopup(offer: IOffer, options: IShowOfferOptions): void {
        this.closeCurrentPopup();
        this._shownOffer = offer;

        const dialogHandler = this.services.dialog.showModal(() => {
            return this._renderBanner(offer);
        }, {
            title: ' ',
            onClosed: () => {
                this.closeCurrentPopup();
                options?.onPopupClosed && options?.onPopupClosed();
            },
            hideCloseButton: true,
            forceTransparentBackground: true,
            disableFullscreenMode: true,
            forceCentralization: true,
            hideDialogHeader: true,
            applyMaxHeight: true,
            isHighPriority: options.isHighPriority ?? false
        });
        
        this._setCurrentPopup(dialogHandler);
    }

    private _renderBanner(offer: IOffer) {
        return (
            <LinkToOfferBannerBox className={'cy-link-to-offer-banner-box'}>
                <CmsBannerComponent key={`LINK_TO_OFFER_BANNER_${offer.id}`} fallbackComponent={""}
                                    templateID={offer.bannerId.templateID}
                                    componentID={offer.bannerId.componentID}
                                    showPlaceholder={false}/>
            </LinkToOfferBannerBox>
        );
    }

    @observable.ref
    currentPopup: IModalDialogHandler | null = null;

    @action.bound
    private _setCurrentPopup(currentPopup: IModalDialogHandler): void {
        this.currentPopup = currentPopup;
    }

    get hasOpenedPopup(): boolean {
        return !Check.isNullOrUndefined(this.currentPopup);
    }

    get isHighPriorityOffer(): boolean {
        return this._shownOffer?.isHighPriorityOffer() ?? false;
    }

    private _registrationPopupTimerId = 0;
    private _preventShowingRegistrationPopupThisDemoSession: boolean = false;

    private _initPopupTimer() {
        this._registrationPopupTimerId = namedSetTimeout('REGISTRATION_POPUP_TIMER', async () => {
            await this._showRegistrationPopup();
            this._preventShowingRegistrationPopupThisDemoSession = true;
        }, 60 * 1000);
    }

    private async _showRegistrationPopup() {
        const offer = new RegistrationOffer(this.services, this.services.configuration.data.registrationOffer);

        const hasBannerContent = await this.services.cmsBanners.loadBanner(offer.bannerId);
        if (!hasBannerContent) {
            this.services.logger.error(`Could not show registration offer, CMS content not available!`, {...this.services.configuration.data.registrationOffer});
            return;
        }

        if (!offer.shouldBeVisible()) {
            return;
        }

        this.services.analytics.sendAnalytics("Conversion", "Show", "DemoRegPopup");

        const options: IShowOfferOptions = {
            onPopupClosed: () => {
                const gameTitle = removeSpecialCharacters(this.services.runningGame.lastOpenDemoGame?.title ?? '');
                this.services.analytics.sendAnalytics("Conversion", `${gameTitle}_Close`, "DemoRegPopup");
            },
            isHighPriority: true
        };
        this.showOfferPopup(offer, options);
    }

    closeCurrentPopup() {
        if (this._registrationPopupTimerId) {
            clearTimeout(this._registrationPopupTimerId);
            this._registrationPopupTimerId = 0;
        }

        if (this.currentPopup) {
            this.currentPopup.close();
            this._setCurrentPopup(null);
            this._shownOffer = null;
        }
    };
}
